// KatibuDashboard.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider,
  FormControl, InputLabel, MenuItem, Select
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TaskIcon from "@mui/icons-material/Task";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler
} from "chart.js";
import LoadingSpinner from "../../components/LoadingSpinner";
import { getLoans } from "../../services/loanService";
import { fetchPrograms } from "../../services/specialContributionService";
import {
  fetchGroupTransactions,
  fetchDeskOfficerNotifications,
} from "../../services/deskOfficerService";
import { fetchGroupTerms } from "../../services/groupService";
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);
// ===== THEME =====
const brand = "#800b33";       // maroon / deep burgundy
const brandHover = "#6c0a2b";  // darker hover

// ===== UTILITIES =====
const formatCurrency = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("en-TZ").format(n) : n;

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return "just now";
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return `${Math.round(diffMs / 1000)}s ago`;
  if (diffMs < hour) return `${Math.round(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.round(diffMs / hour)}h ago`;
  return `${Math.round(diffMs / day)}d ago`;
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const getMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const buildMonthBuckets = (count) => {
  const buckets = [];
  const today = new Date();
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const bucketDate = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    buckets.push({
      key: getMonthKey(bucketDate),
      label: bucketDate.toLocaleDateString("en-TZ", { month: "short", year: "numeric" }),
    });
  }
  return buckets;
};

const KATIBU_PENDING_LOAN_STATUSES = ["PENDING_SECRETARY_APPROVAL"];
const KATIBU_PROCESSED_LOAN_STATUSES = [
  "PENDING_ACCOUNTANT_APPROVAL",
  "APPROVED_FOR_DISBURSEMENT",
  "ACTIVE",
  "CLOSED",
  "REJECTED",
];
const PROGRAM_PENDING_STATUS = "PENDING_SECRETARY_APPROVAL";
const PROGRAM_ACTIVE_STATUSES = [
  "PENDING_CHAIR_APPROVAL",
  "PENDING_SECRETARY_APPROVAL",
  "PENDING_ACCOUNTANT_APPROVAL",
  "ACTIVE",
];

const buildKatibuSeries = (transactions = [], loans = [], count = 8) => {
  const buckets = buildMonthBuckets(count);
  const contributions = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});
  const approvals = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});

  transactions.forEach((tx) => {
    if (!tx?.created_at) return;
    const txDate = new Date(tx.created_at);
    if (Number.isNaN(txDate.getTime())) return;
    const key = getMonthKey(txDate);
    if (!Object.prototype.hasOwnProperty.call(contributions, key)) return;
    const amount = Math.abs(Number(tx.amount) || 0);
    if (tx.transaction_type === "MEMBER_CONTRIBUTION" || tx.transaction_type === "CONTRIBUTION") {
      contributions[key] += amount;
    }
  });

  loans.forEach((loan) => {
    const dateValue = loan.updated_at || loan.approved_at || loan.created_at;
    if (!dateValue) return;
    const loanDate = new Date(dateValue);
    if (Number.isNaN(loanDate.getTime())) return;
    const key = getMonthKey(loanDate);
    if (!Object.prototype.hasOwnProperty.call(approvals, key)) return;
    if (!KATIBU_PROCESSED_LOAN_STATUSES.includes(loan.status)) return;
    approvals[key] += Math.abs(Number(loan.principal_amount) || 0);
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    contributions: buckets.map((bucket) => contributions[bucket.key] || 0),
    approvals: buckets.map((bucket) => approvals[bucket.key] || 0),
  };
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-TZ", { dateStyle: "medium", timeStyle: "short" });
};

const getLoanMemberName = (loan) =>
  loan.member_name ||
  loan.member_full_name ||
  loan.member?.full_name ||
  loan.member?.name ||
  loan.member ||
  "—";

const getGroupName = (item) =>
  item.group?.name ||
  item.group_name ||
  item.group?.group_name ||
  item.group ||
  "—";

const getLoanType = (loan) =>
  loan.loan_type ||
  loan.product_name ||
  loan.product?.name ||
  loan.type ||
  "Loan";

const formatProgramAmount = (program) => {
  const perMember = Number(program.target_per_member);
  if (Number.isFinite(perMember) && perMember > 0) {
    return `${formatCurrency(perMember)} / member`;
  }
  if (program.total_target) {
    return formatCurrency(Number(program.total_target));
  }
  return "—";
};

const loanStatusChipColor = (status) => {
  if (status === "REJECTED") return "error";
  if (KATIBU_PROCESSED_LOAN_STATUSES.includes(status)) return "success";
  if (status?.includes("PENDING")) return "warning";
  return "default";
};

// ===== REUSABLE CARD =====
const StatCard = ({ icon, label, value, index }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
        transition: "all .25s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `${brand}1A`, // subtle brand tint
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {formatCurrency(value)}
          </Typography>
        </Box>
      </Box>
    </Card>
  </motion.div>
);

const KatibuDashboard = () => {
  const [loans, setLoans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        setTerms(normalizeListResponse(payload));
      })
      .catch((err) => console.error("Failed to load terms:", err));
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);

  useEffect(() => {
    if (selectedTermId === "current" && !effectiveTermId) {
      return undefined;
    }
    const controller = new AbortController();

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const termFilter = effectiveTermId ? { term_id: effectiveTermId } : {};
        const [loanPayload, programPayload, transactionsPage, notificationsPage] = await Promise.all([
          getLoans(termFilter),
          fetchPrograms(termFilter),
          fetchGroupTransactions({ ...termFilter, signal: controller.signal }),
          fetchDeskOfficerNotifications({ signal: controller.signal }),
        ]);

        setLoans(
          Array.isArray(loanPayload)
            ? loanPayload
            : loanPayload?.results ?? loanPayload?.data ?? []
        );
        setPrograms(
          Array.isArray(programPayload)
            ? programPayload
            : programPayload?.results ?? programPayload?.data ?? []
        );
        setTransactions(normalizeListResponse(transactionsPage));
        setNotifications(normalizeListResponse(notificationsPage));
        setError(null);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("KatibuDashboard error:", err);
          setError(err.message || "Unable to load dashboard data.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, [effectiveTermId, selectedTermId]);

  const pendingLoans = useMemo(
    () => loans.filter((loan) => KATIBU_PENDING_LOAN_STATUSES.includes(loan.status)),
    [loans]
  );
  const pendingPrograms = useMemo(
    () => programs.filter((program) => program.status === PROGRAM_PENDING_STATUS),
    [programs]
  );
  const processedLoans = useMemo(
    () => loans.filter((loan) => KATIBU_PROCESSED_LOAN_STATUSES.includes(loan.status)),
    [loans]
  );
  const activePrograms = useMemo(
    () => programs.filter((program) => PROGRAM_ACTIVE_STATUSES.includes(program.status)),
    [programs]
  );
  const notificationItems = useMemo(
    () =>
      notifications.map((notification) => ({
        id:
          notification.id ??
          notification.pk ??
          `${notification.type ?? "notification"}-${notification.created_at ?? notification.timestamp}`,
        text: notification.message || notification.text || notification.title || "Notification",
        time: formatTimeAgo(notification.created_at || notification.timestamp),
      })),
    [notifications]
  );
  const processedThisWeek = useMemo(() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return processedLoans.filter((loan) => {
      const reference = new Date(loan.updated_at || loan.approved_at || loan.created_at).getTime();
      return Number.isFinite(reference) && reference >= oneWeekAgo;
    }).length;
  }, [processedLoans]);
  const handleReviewPending = () => navigate("/app/katibu/loan-approvals");
  const handleViewLoans = () => navigate("/app/loan-approvals");
  const handleExportRegistrations = () => navigate("/app/chairperson/members");
  const stats = useMemo(
    () => [
      {
        label: "Pending Approvals",
        value: pendingLoans.length,
        icon: <AssignmentTurnedInIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: "Processed This Week",
        value: processedThisWeek,
        icon: <TaskIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: "Pending Contributions",
        value: pendingPrograms.length,
        icon: <MailOutlinedIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: "Active Programs",
        value: activePrograms.length,
        icon: <GroupsIcon sx={{ fontSize: 28, color: brand }} />,
      },
    ],
    [pendingLoans.length, processedThisWeek, pendingPrograms.length, activePrograms.length]
  );
  const pendingItems = useMemo(() => {
    const rows = [];
    pendingLoans.slice(0, 4).forEach((loan) => {
      rows.push({
        id: `loan-${loan.id}`,
        type: `${getLoanType(loan)} (Loan)`,
        group: getGroupName(loan),
        member: getLoanMemberName(loan),
        amount: loan.principal_amount,
        since: loan.created_at,
      });
    });
    pendingPrograms.slice(0, 4).forEach((program) => {
      rows.push({
        id: `program-${program.id}`,
        type: "Special Contribution",
        group: getGroupName(program),
        member: program.title || "Contribution Program",
        amount: formatProgramAmount(program),
        since: program.created_at || program.start_date,
      });
    });
    return rows.slice(0, 6);
  }, [pendingLoans, pendingPrograms]);
  const processedItems = useMemo(
    () =>
      [...processedLoans]
        .sort((a, b) => {
          const dateB = new Date(b.updated_at || b.approved_at || b.created_at).getTime() || 0;
          const dateA = new Date(a.updated_at || a.approved_at || a.created_at).getTime() || 0;
          return dateB - dateA;
        })
        .slice(0, 4)
        .map((loan) => ({
          id: loan.id,
          type: `${getLoanType(loan)} (Loan)`,
          status: loan.status,
          date: loan.updated_at || loan.approved_at || loan.created_at,
        })),
    [processedLoans]
  );
  const chartSeries = useMemo(() => buildKatibuSeries(transactions, loans), [transactions, loans]);
  const chartData = useMemo(
    () => ({
      labels: chartSeries.labels,
      datasets: [
        {
          label: "Approvals (TZS)",
          data: chartSeries.approvals,
          borderColor: brand,
          backgroundColor: `${brand}24`,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
        {
          label: "Contributions (TZS)",
          data: chartSeries.contributions,
          borderColor: "#0d47a1",
          backgroundColor: "rgba(13,71,161,.12)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
      ],
    }),
    [chartSeries]
  );
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } },
      x: { grid: { display: false } },
    },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Title */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            Katibu Dashboard
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Review approvals, track activity, and coordinate with Chair & Muhasibu
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Term</InputLabel>
          <Select
            value={selectedTermId}
            label="Term"
            onChange={(event) => setSelectedTermId(event.target.value)}
          >
            <MenuItem value="current">Current term</MenuItem>
            <MenuItem value="all">All terms</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `Term ${term.term_number}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <LoadingSpinner />
        </Box>
      )}

      {error && !loading && (
        <Typography color="error" sx={{ mb: 3 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {stats.map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={s.label}>
                <StatCard icon={s.icon} label={s.label} value={s.value} index={i} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Approvals vs Contributions (trend)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300 }}>
                    <Line data={chartData} options={chartOptions} />
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.2}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Quick Actions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                      onClick={handleViewLoans}
                    >
                      View Active Loans
                    </Button>

                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: brand,
                        color: brand,
                        "&:hover": { borderColor: brandHover, color: brandHover },
                      }}
                      onClick={handleExportRegistrations}
                    >
                      Registrations
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={7}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.4}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Pending Approvals
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                      >
                        View All
                      </Button>
                    </Box>
                    <TableContainer component={Paper} elevation={0}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Group</TableCell>
                            <TableCell>Member / Detail</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Since</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">
                                  No items require your attention right now.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            pendingItems.map((req, idx) => (
                              <TableRow key={req.id} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{req.type}</TableCell>
                                <TableCell>{req.group}</TableCell>
                                <TableCell>{req.member}</TableCell>
                                <TableCell>
                                  {typeof req.amount === "number"
                                    ? formatCurrency(req.amount)
                                    : req.amount}
                                </TableCell>
                                <TableCell>{formatDateTime(req.since)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.6}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                      Recently Processed
                    </Typography>
                    <TableContainer component={Paper} elevation={0}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {processedItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" color="text.secondary">
                                  No recent activity has been tracked yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            processedItems.map((p, idx) => (
                              <TableRow key={p.id} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{p.type}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={p.status}
                                    size="small"
                                    color={loanStatusChipColor(p.status)}
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>{formatDateTime(p.date)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.8}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                      Notifications
                    </Typography>
                    {notificationItems.map((n) => (
                      <motion.div
                        key={n.id}
                        whileHover={{ scale: 1.02 }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px",
                          borderRadius: "10px",
                          background: "#f9fafb",
                          marginBottom: "10px",
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <NotificationsActiveIcon sx={{ color: brand, marginRight: 12 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {n.text}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {n.time}
                          </Typography>
                        </Box>
                      </motion.div>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", color: "text.secondary", mt: 4 }}
          >
            © {new Date().getFullYear()} Savvy Community
          </Typography>
        </>
      )}
    </Box>
  );
};

export default KatibuDashboard;
